import { getSupabaseBaseUrl, supabase } from "@/services/supabaseClient";

const PATIENT_DEPENDANT_AVATAR_BUCKET = "patient-profile-images";
const DEPENDANT_AVATAR_PLACEHOLDER = "/icons/circular-avatar-placeholder.svg";

function safeText(value) {
  return String(value || "").trim();
}

function firstNonEmpty(values, fallback = "") {
  for (const value of values) {
    if (value === 0 || value === false) return value;
    if (value != null && String(value).trim() !== "") return value;
  }
  return fallback;
}

export function resolveDependantAvatarSrc(row = {}, fallback = DEPENDANT_AVATAR_PLACEHOLDER) {
  const directUrl = safeText(firstNonEmpty([row?.avatarSrc, row?.avatarUrl, row?.avatar_url]));
  if (directUrl) return directUrl;

  const path = safeText(firstNonEmpty([row?.avatarPath, row?.avatar_path]));
  if (!path) return fallback;
  if (/^https?:\/\//i.test(path)) return path;

  const base = getSupabaseBaseUrl();
  if (!base) return fallback;
  return `${base}/storage/v1/object/public/${PATIENT_DEPENDANT_AVATAR_BUCKET}/${path.replace(/^public\//, "")}`;
}

function calcAge(dateOfBirth) {
  const dob = dateOfBirth ? new Date(dateOfBirth) : null;
  if (!dob || Number.isNaN(dob.getTime())) return null;

  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age >= 0 && age <= 120 ? age : null;
}

function buildGuardianName(auth = {}) {
  return safeText(
    firstNonEmpty(
      [
        auth?.profile?.full_name,
        auth?.profile?.display_name,
        auth?.user?.user_metadata?.full_name,
        auth?.user?.email,
        "Patient",
      ],
      "Patient",
    ),
  );
}

export function normalizeDependantRecord(row = {}) {
  const firstName = safeText(firstNonEmpty([row?.first_name, row?.firstName]));
  const lastName = safeText(firstNonEmpty([row?.last_name, row?.lastName]));
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
  const dateOfBirth = safeText(firstNonEmpty([row?.date_of_birth, row?.dateOfBirth]));
  const relationship = safeText(row?.relationship);

  return {
    id: safeText(row?.id),
    firstName,
    lastName,
    fullName: safeText(firstNonEmpty([row?.fullName, row?.patientName, fullName])) || "Dependant",
    avatarPath: safeText(firstNonEmpty([row?.avatar_path, row?.avatarPath])),
    avatarUrl: safeText(firstNonEmpty([row?.avatar_url, row?.avatarUrl])),
    avatarSrc: safeText(firstNonEmpty([row?.avatarSrc, row?.avatar_src])),
    relationship: relationship || "Dependant",
    dateOfBirth: dateOfBirth || null,
    age: calcAge(dateOfBirth),
    verificationStatus: safeText(row?.verification_status) || "unverified",
    isActive: row?.is_active !== false,
    raw: row,
  };
}

export async function hydrateDependantAvatar(row = {}, client = supabase) {
  const path = safeText(firstNonEmpty([row?.avatar_path, row?.avatarPath])).replace(/^public\//, "");
  if (!path) {
    return {
      ...row,
      avatarSrc: resolveDependantAvatarSrc(row),
    };
  }

  try {
    const { data, error } = await client.storage
      .from(PATIENT_DEPENDANT_AVATAR_BUCKET)
      .createSignedUrl(path, 60 * 60 * 24);

    if (!error && data?.signedUrl) {
      return {
        ...row,
        avatar_path: path,
        avatarSrc: data.signedUrl,
      };
    }
  } catch {
    // Public URL fallback below covers public buckets and legacy rows.
  }

  return {
    ...row,
    avatar_path: path,
    avatarSrc: resolveDependantAvatarSrc({ ...row, avatar_path: path }),
  };
}

export async function listGuardianDependants(auth = {}) {
  const guardianUserId = safeText(auth?.user?.id);
  if (!guardianUserId) return [];

  const { data, error } = await supabase
    .from("patient_dependants")
    .select("*")
    .eq("user_id", guardianUserId)
    .order("first_name", { ascending: true });

  if (error) throw error;

  const hydratedRows = await Promise.all(
    (Array.isArray(data) ? data : []).map((row) => hydrateDependantAvatar(row)),
  );

  return hydratedRows
    .map((row) => normalizeDependantRecord(row))
    .filter((row) => row.isActive !== false);
}

export function buildSelfCareSubject(auth = {}) {
  const guardianUserId = safeText(auth?.user?.id);
  const guardianName = buildGuardianName(auth);
  const guardianEmail = safeText(auth?.user?.email || auth?.profile?.email);

  return {
    selectionId: "self",
    type: "self",
    dependantId: null,
    patientName: guardianName || "Patient",
    relationship: "Self",
    dateOfBirth: safeText(auth?.profile?.date_of_birth) || null,
    age: calcAge(auth?.profile?.date_of_birth),
    guardianUserId,
    guardianName,
    guardianEmail,
    responsibleUserId: guardianUserId,
    responsibleRelationship: "Self",
    isDependant: false,
    isMinor: false,
  };
}

export function buildDependantCareSubject(dependant = {}, auth = {}) {
  const normalized = normalizeDependantRecord(dependant);
  const guardianUserId = safeText(auth?.user?.id);
  const guardianName = buildGuardianName(auth);
  const guardianEmail = safeText(auth?.user?.email || auth?.profile?.email);

  return {
    selectionId: normalized.id,
    type: "dependant",
    dependantId: normalized.id,
    firstName: normalized.firstName,
    lastName: normalized.lastName,
    avatarPath: normalized.avatarPath || "",
    avatarUrl: normalized.avatarUrl || "",
    avatarSrc: normalized.avatarSrc || resolveDependantAvatarSrc(normalized),
    patientName: normalized.fullName,
    relationship: normalized.relationship || "Dependant",
    dateOfBirth: normalized.dateOfBirth,
    age: normalized.age,
    guardianUserId,
    guardianName,
    guardianEmail,
    responsibleUserId: guardianUserId,
    responsibleRelationship: normalized.relationship || "Guardian",
    verificationStatus: normalized.verificationStatus,
    isDependant: true,
    isMinor: normalized.age != null ? normalized.age < 18 : false,
  };
}

export function normalizeCareSubjectFromBooking(booking = {}, auth = {}) {
  const raw = booking?.careSubject && typeof booking.careSubject === "object" ? booking.careSubject : null;

  if (raw?.type === "dependant" || booking?.careSubjectType === "dependant") {
    const resolvedDob =
      safeText(raw?.dateOfBirth || booking?.careSubjectDateOfBirth || booking?.dateOfBirth) || null;
    const resolvedAge = calcAge(resolvedDob);
    return {
      selectionId: safeText(raw?.selectionId || raw?.dependantId || booking?.careSubjectId || booking?.dependantId),
      type: "dependant",
      dependantId: safeText(raw?.dependantId || booking?.careSubjectId || booking?.dependantId),
      firstName: safeText(raw?.firstName || raw?.first_name || booking?.careSubjectFirstName),
      lastName: safeText(raw?.lastName || raw?.last_name || booking?.careSubjectLastName),
      avatarPath: safeText(raw?.avatarPath || raw?.avatar_path || booking?.careSubjectAvatarPath),
      avatarUrl: safeText(raw?.avatarUrl || raw?.avatar_url || booking?.careSubjectAvatarUrl),
      avatarSrc: safeText(raw?.avatarSrc || raw?.avatar_src || booking?.careSubjectAvatarSrc) || resolveDependantAvatarSrc({
        ...(booking || {}),
        ...(raw || {}),
        avatarPath: raw?.avatarPath || raw?.avatar_path || booking?.careSubjectAvatarPath,
        avatarUrl: raw?.avatarUrl || raw?.avatar_url || booking?.careSubjectAvatarUrl,
      }),
      patientName: safeText(raw?.patientName || booking?.careSubjectName || booking?.patientName) || "Dependant",
      relationship: safeText(raw?.relationship || booking?.careSubjectRelationship || booking?.relationship) || "Dependant",
      dateOfBirth: resolvedDob,
      age: resolvedAge,
      guardianUserId: safeText(raw?.guardianUserId || auth?.user?.id),
      guardianName: safeText(raw?.guardianName || buildGuardianName(auth)),
      guardianEmail: safeText(raw?.guardianEmail || auth?.user?.email || auth?.profile?.email),
      responsibleUserId: safeText(raw?.responsibleUserId || raw?.guardianUserId || auth?.user?.id),
      responsibleRelationship:
        safeText(raw?.responsibleRelationship || raw?.relationship || booking?.careSubjectRelationship) || "Guardian",
      verificationStatus: safeText(raw?.verificationStatus || booking?.careSubjectVerificationStatus) || "unverified",
      isDependant: true,
      isMinor: resolvedAge != null ? resolvedAge < 18 : false,
    };
  }

  return buildSelfCareSubject(auth);
}

export function buildBookingCareSubjectOptions(dependants = [], auth = {}) {
  const self = buildSelfCareSubject(auth);
  const dependantOptions = (Array.isArray(dependants) ? dependants : []).map((dependant) =>
    buildDependantCareSubject(dependant, auth),
  );
  return [self, ...dependantOptions];
}
